/**
 * Whether toggling this invoice reverts it to a draft. InvoiceHelper.onToggleFinalize and
 * the invoice card both decide with this, so every toggle that unfinalizes is asked about,
 * including an inconsistent invoice ('F' without a finalized date) that the toggle repairs
 * by unfinalizing.
 */
export function togglesToDraft(invoice: {invoiceStatus?: string | null, finalizedOn?: unknown}): boolean {
    if (invoice.invoiceStatus === 'F') return true;
    if (invoice.invoiceStatus === 'D') return false;
    return !!invoice.finalizedOn;
}

/**
 * What clicking Finalize or Unfinalize on an invoice card must do first.
 *
 * - `proceed`: finalizing a draft, which needs no confirmation.
 * - `blocked`: only an account owner may unfinalize an invoice that has been sent.
 * - `confirmSent`: unfinalizing a sent invoice, whose client already holds a copy.
 * - `confirm`: unfinalizing an invoice that was never sent. A finalized invoice is the
 *   document of record, so reopening it always asks (OWA-1165).
 */
export type UnfinalizeGuard = 'proceed' | 'blocked' | 'confirmSent' | 'confirm';

export function unfinalizeGuard(
    invoice: {sentDate?: Date | null, sentUsing?: string | null},
    unfinalizes: boolean,
    isOwner: boolean,
): UnfinalizeGuard {
    if (!unfinalizes) return 'proceed';

    const hasBeenSent = !!(invoice.sentDate || invoice.sentUsing);
    if (!hasBeenSent) return 'confirm';
    return isOwner ? 'confirmSent' : 'blocked';
}
